import * as z from "zod";

import { getDashboardSurface } from "./dashboardRoutes";

export const LAST_VISITED_DASHBOARD_ROUTE_COOKIE = "replo_dashboard_route";
// TODO (Yuxin, 2026-10-01, REPL-33368): delete with readLegacyProjectId once pre-cookie browsers have migrated.
export const LEGACY_LAST_VISITED_PROJECT_ID_KEY =
  "replo.project.lastVisitedProjectId";

// The cookie is the only record of the last project, so it lives as long as browsers allow.
const LAST_VISITED_DASHBOARD_ROUTE_MAX_AGE_SECONDS = 60 * 60 * 24 * 400;
const dashboardRouteSchema = z.object({
  projectId: z.uuid(),
  surface: z.enum(["v1", "v2", "agent"]),
});

function readCookie(name: string): string | null {
  for (const part of document.cookie.split(";")) {
    const separatorIndex = part.indexOf("=");
    if (
      separatorIndex !== -1 &&
      part.slice(0, separatorIndex).trim() === name
    ) {
      return part.slice(separatorIndex + 1).trim();
    }
  }
  return null;
}

function readLegacyProjectId(): string | null {
  try {
    const result = z
      .uuid()
      .safeParse(
        window.localStorage.getItem(LEGACY_LAST_VISITED_PROJECT_ID_KEY),
      );
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

// (Noah, 2025-10-23): Read once per navigation decision; a reactive read refetches user.get and flashes loading states.
export function getLandingUserQueryInput(): {
  lastVisitedProjectId: string | null;
} {
  const [surface, projectId, ...extra] =
    readCookie(LAST_VISITED_DASHBOARD_ROUTE_COOKIE)?.split(":") ?? [];
  const result = dashboardRouteSchema.safeParse({ projectId, surface });
  return {
    lastVisitedProjectId:
      result.success && extra.length === 0
        ? result.data.projectId
        : readLegacyProjectId(),
  };
}

export function clearLastVisitedDashboardRoute() {
  document.cookie = `${LAST_VISITED_DASHBOARD_ROUTE_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
}

export function setLastVisitedDashboardRoute(project: {
  id: string;
  hostingType: string | null;
  agentEnabledAt?: Date | string | null;
}) {
  const surface = getDashboardSurface({
    hostingType: project.hostingType,
    agentEnabledAt: project.agentEnabledAt ?? null,
  });
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${LAST_VISITED_DASHBOARD_ROUTE_COOKIE}=${surface}:${project.id}; path=/; max-age=${LAST_VISITED_DASHBOARD_ROUTE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  try {
    window.localStorage.removeItem(LEGACY_LAST_VISITED_PROJECT_ID_KEY);
  } catch {
    // Storage blocked: there is no legacy id to migrate.
  }
}

// Logout keeps the last project (the server ignores it for other accounts) but drops the unchecked v1 workspace id.
export function clearLastVisitedWorkspaceId() {
  try {
    window.localStorage.removeItem("replo.workspace.lastvisitedWorkspaceId");
  } catch {
    // ignore
  }
}
